import { useCallback, useMemo } from "react";
import { View, Text, Platform, Pressable, ScrollView } from "react-native";
import {
  Search,
  ClipboardCheck,
  HeartPulse,
  Snowflake,
  Building2,
  Users,
  History,
  Map,
  Compass,
  Gem,
  HeartHandshake,
  Heart,
  Home,
  CalendarClock,
  PlayCircle,
} from "lucide-react-native";
import { KebabMenu, type KebabMenuItem } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../../../onboarding/theme";
import { useYouScreen } from "../YouScreenContext";
import { useActiveCycle, useCycles } from "@src/packages/cycles";
import {
  deriveCyclePhase,
  resolveJourneyPath,
  type JourneyPath,
} from "../modals/RoadmapModal";
import { useStartTreatmentController } from "../../../StartTreatmentController";
import { useAuthorizedExperienceProjection } from "@src/packages/experience/hooks/useAuthorizedExperienceProjection";

const NON_CYCLE_GOALS = new Set(["adoption", "surrogacy"]);

const PHASES = [
  {
    id: "deciding",
    labelKey: "you.my_journey.phase_deciding_label",
    icon: Search,
    headingKey: "you.my_journey.phase_deciding_heading",
    messageKey: "you.my_journey.phase_deciding_message",
  },
  {
    id: "preparing",
    labelKey: "you.my_journey.phase_preparing_label",
    icon: ClipboardCheck,
    headingKey: "you.my_journey.phase_preparing_heading",
    messageKey: "you.my_journey.phase_preparing_message",
  },
  {
    id: "in-treatment",
    labelKey: "you.my_journey.phase_in_treatment_label",
    icon: HeartPulse,
    headingKey: "you.my_journey.phase_in_treatment_heading",
    messageKey: "you.my_journey.phase_in_treatment_message",
  },
  {
    id: "storing",
    labelKey: "you.my_journey.phase_storing_label",
    icon: Snowflake,
    headingKey: "you.my_journey.phase_storing_heading",
    messageKey: "you.my_journey.phase_storing_message",
  },
] as const;

/**
 * The last rung, which is not the same thing on both paths.
 *
 * Four rungs either way — the map's shape, its rail and its illustrations are
 * untouched. Only what the final one says changes: a freezing cycle ends with
 * material in storage, a transfer ends with a result, and a snowflake reading
 * "Storing" to a patient who has just had an embryo transferred describes
 * nothing she is living.
 */
const TERMINAL_PHASE_BY_PATH = {
  preserve: {
    labelKey: "you.my_journey.phase_storing_label",
    icon: Snowflake,
    headingKey: "you.my_journey.phase_storing_heading",
    messageKey: "you.my_journey.phase_storing_message",
  },
  pregnancy: {
    labelKey: "you.my_journey.phase_result_label",
    icon: Heart,
    headingKey: "you.my_journey.phase_result_heading",
    messageKey: "you.my_journey.phase_result_message",
  },
} as const;

function phasesForPath(path: JourneyPath) {
  return PHASES.map((phase) =>
    phase.id === "storing"
      ? { ...phase, ...TERMINAL_PHASE_BY_PATH[path] }
      : phase,
  );
}

const INTENT_LABEL_KEYS: Record<string, string> = {
  // New goal values
  exploring: "you.my_journey.intent_exploring",
  preserve: "you.my_journey.intent_preservation",
  "egg-preserve": "you.my_journey.intent_egg_freezing",
  "sperm-preserve": "you.my_journey.intent_sperm_freezing",
  "embryo-preserve": "you.my_journey.intent_embryo_freezing",
  pregnancy: "you.my_journey.intent_pregnancy",
  "egg-donation": "you.my_journey.intent_egg_donation",
  "sperm-donation": "you.my_journey.intent_sperm_donation",
  adoption: "you.my_journey.intent_adoption",
  surrogacy: "you.my_journey.intent_surrogacy",
  // Legacy intent values
  Exploring: "you.my_journey.intent_exploring",
  Preservation: "you.my_journey.intent_preservation",
  Donation: "you.my_journey.intent_donation",
  Conception: "you.my_journey.intent_conception",
  StartingTreatment: "you.my_journey.intent_starting_treatment",
  InTreatment: "you.my_journey.intent_in_treatment",
};

const INTENT_ICONS: Record<string, typeof Snowflake> = {
  // New goal values
  exploring: Compass,
  preserve: Snowflake,
  "egg-preserve": Snowflake,
  "sperm-preserve": Snowflake,
  "embryo-preserve": Gem,
  pregnancy: HeartHandshake,
  "egg-donation": Heart,
  "sperm-donation": Heart,
  adoption: Home,
  surrogacy: Users,
  // Legacy intent values
  Exploring: Compass,
  Preservation: Snowflake,
  Donation: Heart,
  Conception: HeartHandshake,
  StartingTreatment: CalendarClock,
  InTreatment: HeartPulse,
};

function getPhaseFromIntent(
  intent: string | null,
): (typeof PHASES)[number]["id"] {
  switch (intent) {
    // New goal values
    case "exploring":
      return "deciding";
    case "preserve":
    case "embryo-preserve":
    case "pregnancy":
      return "preparing";
    case "egg-donation":
    case "sperm-donation":
      return "preparing";
    case "adoption":
    case "surrogacy":
      return "deciding";
    // Legacy intent values
    case "Exploring":
      return "deciding";
    case "Preservation":
    case "Conception":
      return "preparing";
    case "StartingTreatment":
    case "InTreatment":
      return "in-treatment";
    case "Donation":
      return "preparing";
    default:
      return "deciding";
  }
}

// Pill chrome ported from LearnScreen filter chips
const INTENT_PILL_BG = "rgba(59,26,8,0.12)";
const STATUS_PILL_BG = "rgba(255,255,255,0.4)";
const STATUS_PILL_BORDER = "rgba(0,0,0,0.06)";

/** Reusable status pill — filled when data exists, dashed outline when empty */
function StatusPill({
  label,
  icon: Icon,
  filled,
  onPress,
}: {
  label?: string;
  icon: typeof Building2;
  filled: boolean;
  onPress: () => void;
}) {
  const capturePress = usePressCapture("StatusPill");
  const isIconOnly = !label;
  return (
    <Pressable
      onPress={(e) => {
        capturePress(pressLabel(label), "journey_status_pill");
        e.stopPropagation();
        onPress();
      }}
      hitSlop={4}
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: isIconOnly ? 0 : 6,
        paddingVertical: 6,
        paddingHorizontal: isIconOnly ? 6 : 12,
        borderRadius: 999,
        borderWidth: 1,
        borderColor: filled ? STATUS_PILL_BORDER : EMBER.colors.border,
        borderStyle: filled ? "solid" : "dashed",
        backgroundColor: filled ? STATUS_PILL_BG : "transparent",
      }}
    >
      <Icon
        size={13}
        color={filled ? EMBER.colors.textMid : EMBER.colors.textFaint}
        strokeWidth={1.8}
      />
      {label && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 13,
            fontWeight: "400",
            color: filled ? EMBER.colors.textMid : EMBER.colors.textFaint,
          }}
        >
          {label}
        </Text>
      )}
    </Pressable>
  );
}

export default function MyJourney() {
  const t = useTranslations();
  const capturePress = usePressCapture("MyJourney");
  const { dispatch, patient } = useYouScreen();
  const { data: cycles } = useCycles();
  const { data: activeCycle } = useActiveCycle();
  const { canonical } = useAuthorizedExperienceProjection();
  const { openStartTreatment } = useStartTreatmentController();
  const isWeb = Platform.OS === "web";

  // Intent
  const intent = canonical?.intent.goal ?? null;
  const intentLabel =
    intent && INTENT_LABEL_KEYS[intent] ? t(INTENT_LABEL_KEYS[intent]) : null;
  const IntentIcon = intent ? INTENT_ICONS[intent] : null;

  // Phase — actual cycle state wins over the (often stale) intent.
  const currentPhase = deriveCyclePhase(cycles) ?? getPhaseFromIntent(intent);
  const journeyPath = resolveJourneyPath(cycles, intent);
  const phases = useMemo(() => phasesForPath(journeyPath), [journeyPath]);
  const currentIndex = phases.findIndex((p) => p.id === currentPhase);
  const phase = phases[currentIndex] ?? phases[0]!;

  // Cycles history
  const pastCycles = cycles?.filter((c) => c.status === "closed") ?? [];
  const hasCycles = pastCycles.length > 0;

  // Connections
  const hasClinic = Boolean(activeCycle?.providerId);
  const clinicName = activeCycle?.provider ?? null;
  const hasPartner = canonical?.viewer.partnerConnected ?? false;

  const openRoadmap = () =>
    dispatch({ type: "SHOW_MODAL", modal: "roadmap" as const });

  // The past-cycles pill opens the past cycles list modal (each entry leads
  // to its recap), not the My Journey roadmap modal.
  const openPastCycles = () =>
    dispatch({ type: "SHOW_MODAL", modal: "past-cycles" as const });

  const openIntentPicker = () =>
    dispatch({ type: "SHOW_MODAL", modal: "intent-picker" as const });

  const hasActiveCycle = !!cycles?.some((c) => c.status === "open");
  const goalSupportsCycles = !intent || !NON_CYCLE_GOALS.has(intent);
  const openStartCycle = useCallback(() => {
    openStartTreatment({
      source: "YouScreen",
      goal:
        intent === "preserve" ||
        intent === "egg-preserve" ||
        intent === "sperm-preserve" ||
        intent === "embryo-preserve" ||
        intent === "Preservation"
          ? "preserve"
          : "pregnancy",
      gender:
        patient?.gender === "AMAB"
          ? "male"
          : patient?.gender === "AFAB"
            ? "female"
            : null,
      presetCycleType:
        intent === "egg-preserve"
          ? "egg_freezing"
          : intent === "sperm-preserve"
            ? "sperm_freezing"
            : intent === "embryo-preserve"
              ? "embryo_freezing"
              : null,
    });
  }, [intent, openStartTreatment, patient?.gender]);

  const cycleMenuItems = useMemo<KebabMenuItem[]>(() => {
    const items: KebabMenuItem[] = [];
    if (goalSupportsCycles && !hasActiveCycle) {
      items.push({
        id: "start",
        label: t("you.my_journey.menu_start_cycle"),
        icon: PlayCircle,
        onPress: () => {
          capturePress(
            pressLabel(t("you.my_journey.menu_start_cycle")),
            "you.my_journey.menu_start_cycle",
          );
          openStartCycle();
        },
      });
    }
    if (goalSupportsCycles && hasActiveCycle) {
      items.push({
        id: "end",
        label: t("you.my_journey.menu_end_cycle"),
        icon: ClipboardCheck,
        onPress: () => {
          capturePress(
            pressLabel(t("you.my_journey.menu_end_cycle")),
            "you.my_journey.menu_end_cycle",
          );
          dispatch({ type: "SHOW_MODAL", modal: "end-cycle" });
        },
      });
    }
    items.push({
      id: "journey-map",
      label: t("you.my_journey.menu_journey_map"),
      icon: Map,
      onPress: () => {
        capturePress(
          pressLabel(t("you.my_journey.menu_journey_map")),
          "you.my_journey.menu_journey_map",
        );
        dispatch({ type: "SHOW_MODAL", modal: "roadmap" });
      },
    });
    items.push({
      id: "change-goal",
      label: t("you.my_journey.menu_change_goal"),
      icon: Compass,
      onPress: () => {
        capturePress(
          pressLabel(t("you.my_journey.menu_change_goal")),
          "you.my_journey.menu_change_goal",
        );
        dispatch({ type: "SHOW_MODAL", modal: "intent-picker" });
      },
    });
    return items;
  }, [
    goalSupportsCycles,
    hasActiveCycle,
    capturePress,
    dispatch,
    openStartCycle,
    t,
  ]);

  return (
    <View style={{ marginBottom: 20 }}>
      {/* Glass card — personal status hub */}
      <View
        style={
          isWeb
            ? {
                borderRadius: 14,
                backgroundColor: "rgba(255, 255, 255, 0.45)",
                borderWidth: 1,
                borderColor: "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(40px)",
                WebkitBackdropFilter: "blur(40px)",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.06,
                shadowRadius: 20,
                overflow: "hidden",
                position: "relative",
              }
            : {
                borderRadius: 14,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                borderWidth: 1,
                borderColor: "rgba(255, 255, 255, 0.6)",
                shadowColor: "#000",
                shadowOffset: { width: 0, height: 4 },
                shadowOpacity: 0.06,
                shadowRadius: 20,
                overflow: "hidden",
                position: "relative",
              }
        }
      >
        {/* Corner gradient for depth */}
        {isWeb && (
          <View
            style={{
              position: "absolute",
              bottom: 0,
              right: 0,
              width: "60%",
              height: "60%",
              background:
                "radial-gradient(ellipse at bottom right, rgba(184, 74, 22, 0.06) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
        )}

        {/* Cycle actions kebab — top-right */}
        <View
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 2,
          }}
        >
          <KebabMenu
            items={cycleMenuItems}
            buttonColor={EMBER.colors.textLight}
            accessibilityLabel={t("you.my_journey.menu_a11y")}
          />
        </View>

        {/* Content */}
        <View style={{ padding: 16, paddingRight: 40, paddingBottom: 14 }}>
          {/* Status pills row */}
          <View
            style={{
              flexDirection: "row",
              flexWrap: "wrap",
              gap: 6,
              marginBottom: 12,
            }}
          >
            {/* Intent pill */}
            <Pressable
              accessibilityRole="button"
              testID="my-journey-intent-pill"
              onPress={() => {
                capturePress(
                  pressLabel(intentLabel ?? t("you.my_journey.set_your_path")),
                  "my_journey_intent_pill",
                );
                openIntentPicker();
              }}
              hitSlop={4}
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                paddingVertical: 6,
                paddingHorizontal: 12,
                borderRadius: 999,
                borderWidth: intentLabel ? 0 : 1,
                borderColor: EMBER.colors.border,
                borderStyle: intentLabel ? "solid" : "dashed",
                backgroundColor: intentLabel ? INTENT_PILL_BG : "transparent",
              }}
            >
              {IntentIcon && (
                <IntentIcon
                  size={13}
                  color={
                    intentLabel ? EMBER.colors.text : EMBER.colors.textFaint
                  }
                  strokeWidth={1.8}
                />
              )}
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 13,
                  fontWeight: "600",
                  color: intentLabel
                    ? EMBER.colors.text
                    : EMBER.colors.textFaint,
                }}
              >
                {intentLabel ?? t("you.my_journey.set_your_path")}
              </Text>
            </Pressable>

            {/* History pill */}
            <StatusPill
              icon={History}
              label={
                hasCycles
                  ? t(
                      pastCycles.length === 1
                        ? "you.my_journey.past_cycles_one"
                        : "you.my_journey.past_cycles_other",
                      { params: { count: String(pastCycles.length) } },
                    )
                  : t("you.my_journey.add_history")
              }
              filled={hasCycles}
              onPress={hasCycles ? openPastCycles : openRoadmap}
            />

            {/* Clinic pill — only shown when connected */}
            {hasClinic && (
              <StatusPill
                icon={Building2}
                label={clinicName ?? t("you.my_journey.clinic_linked")}
                filled
                onPress={() =>
                  dispatch({
                    type: "SHOW_DATA_SHARING_MODAL",
                    connectionType: "clinic",
                  })
                }
              />
            )}

            {/* Partner pill — icon-only when connected */}
            {hasPartner && (
              <StatusPill
                icon={Users}
                filled
                onPress={() =>
                  dispatch({
                    type: "SHOW_DATA_SHARING_MODAL",
                    connectionType: "partner",
                  })
                }
              />
            )}
          </View>

          {/* Phase heading */}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 20,
              fontWeight: "600",
              color: EMBER.colors.text,
              letterSpacing: -0.3,
              marginBottom: 4,
            }}
          >
            {t(phase.headingKey)}
          </Text>

          {/* Human message */}
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 14,
              fontWeight: "400",
              color: EMBER.colors.textLight,
              lineHeight: 20,
            }}
          >
            {t(phase.messageKey)}
          </Text>
        </View>

        {/* Bottom row: Map button + Timeline pills */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          className="px-3.5 pb-3.5"
          contentContainerClassName="flex-row items-center"
        >
          {/* Map button — clearly tappable */}
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("you.my_journey.map")),
                "you.my_journey.map",
              );
              openRoadmap();
            }}
            className="mr-2.5 flex-row items-center gap-1.5 rounded-lg bg-[#B84A16]/[0.07] px-2.5 py-1.5"
          >
            <Map size={14} color={EMBER.colors.warm} strokeWidth={1.8} />
            <Text className="font-dm-sans text-xs font-semibold text-[#B84A16]">
              {t("you.my_journey.map")}
            </Text>
          </Pressable>

          {/* Timeline pills — uniform chrome, subtle differentiation for current */}
          {phases.map((p, i) => {
            const isCurrent = i === currentIndex;
            const isCompleted = i < currentIndex;
            const Icon = p.icon;
            const isLast = i === phases.length - 1;
            const tone = isCurrent
              ? EMBER.colors.textMid
              : isCompleted
                ? EMBER.colors.textLight
                : EMBER.colors.textFaint;

            return (
              <View key={p.id} className="flex-row items-center">
                <Pressable
                  onPress={() => {
                    capturePress(
                      pressLabel(t(p.labelKey)),
                      "journey_timeline_phase",
                    );
                    openRoadmap();
                  }}
                  className="flex-row items-center gap-1 rounded-md border border-black/[0.06] bg-white/40 px-2 py-1"
                >
                  <Icon size={11} color={tone} strokeWidth={1.5} />
                  <Text
                    className="font-dm-sans text-[11px]"
                    style={{
                      fontWeight: isCurrent ? "600" : "400",
                      color: tone,
                    }}
                  >
                    {t(p.labelKey)}
                  </Text>
                </Pressable>

                {/* Connector line */}
                {!isLast && <View className="mx-1 h-px w-2 bg-black/[0.06]" />}
              </View>
            );
          })}
        </ScrollView>
      </View>
    </View>
  );
}
